import { Skeleton } from '@mantine/core'
import { ReactQuery } from 'cms-api'
import { useRouter } from 'next/router'
import React from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import slugify from 'slugify'
import styled, { css } from 'styled-components'
import { AddToFavorites } from 'components/AddToFavorites'
import { AddToFavoritesDetailHintPopover } from 'components/AddToFavorites/AddToFavoritesDetailHintPopover'
import { Flex } from 'components/Layout'
import Link from 'components/Link'
import { H3, Text } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, FONT_SIZE, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { CurrencyIsoCode } from 'types/graphql-generated'
import {
  PRICE_UPON_REQUEST,
  formatPriceWithQuantityUnit,
  getDiscountBadge,
} from 'utils/formatters'

interface TitleProps {
  id: number
  brand?: string
  title: string
  showroom?: {
    name: string
    url: string
  }
  price: number
  tradePrice: number
  quantityUnitSingular: string
  isPriceVisible: boolean
  discount?: string
  isLoading?: boolean
}

const StyledPrice = styled(Text)`
  ${({ shouldDisplayTradePrice }) =>
    shouldDisplayTradePrice &&
    css`
      text-decoration: line-through;
    `}
`

export const Title: React.FC<React.PWC<TitleProps>> = ({
  id,
  brand,
  title,
  showroom,
  price,
  tradePrice,
  quantityUnitSingular,
  isPriceVisible,
  discount,
  isLoading,
}) => {
  const { user, isClientSignedIn, isExpertSignedIn } = useUserAuth()
  const router = useRouter()

  const {
    data: favoriteUserProductVariant,
    refetch: refetchFavoriteUserProductVariant,
  } = ReactQuery.useGetFavoriteUserProductVariantBookmarkLegacyProductVariantId(
    id.toString(),
    {
      query: {
        enabled: isClientSignedIn || isExpertSignedIn,
      },
    }
  )

  const quantityUnit = quantityUnitSingular
    ? {
        singular: quantityUnitSingular,
      }
    : undefined
  const formattedPrice =
    price > 0
      ? formatPriceWithQuantityUnit({
          price: { total: price, currencyIsoCode: CurrencyIsoCode.Usd },
          quantityUnit,
        })
      : PRICE_UPON_REQUEST
  const formattedTradePrice =
    tradePrice > 0
      ? formatPriceWithQuantityUnit({
          price: { total: tradePrice, currencyIsoCode: CurrencyIsoCode.Usd },
          quantityUnit,
        })
      : PRICE_UPON_REQUEST

  const isTradePriceAvailable =
    formattedTradePrice !== PRICE_UPON_REQUEST &&
    formattedPrice !== formattedTradePrice
  const shouldDisplayTradePrice = user?.isTradeCapable && isTradePriceAvailable

  const discountBadge = getDiscountBadge({
    price: price ?? 0,
    tradePrice: tradePrice ?? 0,
  })

  const isBookmarked = !!favoriteUserProductVariant?.data?.isBookmarked

  return (
    <Flex flexDirection="column">
      {brand && (
        <Link
          data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_BRAND}
          mb={SPACE.PX_4}
          fontFamily={FONT_STACK.SERIF}
          fontWeight={FONT_WEIGHT.MEDIUM}
          fontSize={FONT_SIZE.PX_13}
          href={ROUTE.SHOWROOM_BRAND_DETAIL({
            slug: slugify(brand, { lower: true, strict: true }),
          })}
        >
          {brand}
        </Link>
      )}
      <Flex justifyContent="space-between" gap={SPACE.PX_20}>
        <H3
          data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_TITLE}
          mb={SPACE.PX_4}
        >
          {title}
        </H3>

        <AddToFavoritesDetailHintPopover>
          <AddToFavorites
            variantId={id}
            isBookmarked={isBookmarked}
            productTitle={title}
            onRefetch={refetchFavoriteUserProductVariant}
          />
        </AddToFavoritesDetailHintPopover>
      </Flex>
      {showroom && (
        <Text fontFamily={FONT_STACK.SERIF} fontWeight={FONT_WEIGHT.MEDIUM}>
          featured in{' '}
          <Link
            href={ROUTE.SHOWROOM_DETAIL({ slug: showroom.url })}
            aria-label={`showroom detail ${showroom.name}`}
          >
            {showroom.name}
          </Link>
        </Text>
      )}
      {isLoading ? (
        <Skeleton height={24} width="150px" radius="8px" my={16} />
      ) : (
        <>
          {isPriceVisible && (
            <>
              {shouldDisplayTradePrice && (
                <>
                  <Text
                    fontSize={FONT_SIZE.PX_16}
                    color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE}
                    mt={SPACE.PX_16}
                    fontWeight={FONT_WEIGHT.MEDIUM}
                  >
                    Trade: {formattedTradePrice}{' '}
                  </Text>
                  {discountBadge > 0 && (
                    <Text
                      fontStyle="italic"
                      color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE}
                      mt={SPACE.PX_4}
                      fontWeight={FONT_WEIGHT.NORMAL}
                    >{`${discountBadge}% off`}</Text>
                  )}
                </>
              )}
              {!user ? (
                <Flex alignItems="center" gap={SPACE.PX_20}>
                  <StyledPrice
                    data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_PRICE}
                    fontSize={
                      shouldDisplayTradePrice
                        ? FONT_SIZE.PX_14
                        : FONT_SIZE.PX_16
                    }
                    color={COLOR_INTENT.PRODUCT_HEADER.PRICE}
                    mt={shouldDisplayTradePrice ? SPACE.PX_4 : SPACE.PX_16}
                    mb={SPACE.PX_16}
                    shouldDisplayTradePrice={shouldDisplayTradePrice}
                  >
                    {shouldDisplayTradePrice
                      ? `Retail: ${formattedPrice}`
                      : formattedPrice}
                  </StyledPrice>{' '}
                  <Text>
                    <Link
                      href={ROUTE.TRADE_SIGN_IN({ returnUrl: router.asPath })}
                    >
                      <Text style={{ textDecoration: 'underline' }}>
                        Log in
                      </Text>
                    </Link>{' '}
                    for trade pricing
                  </Text>
                </Flex>
              ) : (
                <StyledPrice
                  data-test-id={DATA_TEST_ID.SHOWROOM_PRODUCT_DETAIL_PRICE}
                  fontSize={
                    shouldDisplayTradePrice ? FONT_SIZE.PX_14 : FONT_SIZE.PX_16
                  }
                  color={COLOR_INTENT.PRODUCT_HEADER.PRICE}
                  mt={shouldDisplayTradePrice ? SPACE.PX_4 : SPACE.PX_16}
                  mb={SPACE.PX_12}
                  shouldDisplayTradePrice={shouldDisplayTradePrice}
                >
                  {shouldDisplayTradePrice
                    ? `Retail: ${formattedPrice}`
                    : formattedPrice}
                </StyledPrice>
              )}
              {shouldDisplayTradePrice && brand !== 'Apparatus' && (
                <Link
                  fontWeight={FONT_WEIGHT.NORMAL}
                  mb={SPACE.PX_12}
                  href={ROUTE.PRICE_MATCH_PROMISE_TERMS_AND_CONDITIONS()}
                  target="_blank"
                >
                  <Text
                    color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE}
                    fontWeight={FONT_WEIGHT.MEDIUM}
                    fontSize={FONT_SIZE.PX_14}
                  >
                    Price Match Promise
                  </Text>{' '}
                </Link>
              )}
            </>
          )}
        </>
      )}
      {discount && (
        <Text
          fontSize={FONT_SIZE.PX_16}
          color={COLOR_INTENT.PRODUCT_HEADER.DISCOUNT}
          mb={SPACE.PX_16}
          fontWeight={FONT_WEIGHT.MEDIUM}
        >
          {discount}
        </Text>
      )}
    </Flex>
  )
}
